import React, { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';
import { Plus, Search, MapPin, Phone, Mail, Building2, ExternalLink, Edit2, Trash2, X, Save } from 'lucide-react';
import { isOfflineModeEnabled, getCachedData, cacheData } from './offlineCache';

const VendorManagement = ({ companyId }) => {
  const { colors } = useTheme();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [savingVendor, setSavingVendor] = useState(false);
  const [fieldsTouched, setFieldsTouched] = useState({}); // {category: true} after user interaction
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    notes: '',
    category: ''
  });

  useEffect(() => {
    loadVendors();
  }, [companyId]);

  const loadVendors = async (retryCount = 0) => {
    if (!companyId) return;
    setLoading(true);
    // Offline: serve from cache
    if (!navigator.onLine && isOfflineModeEnabled()) {
      const cached = getCachedData('vendors', companyId);
      if (cached) {
        setVendors(cached);
        setLoading(false);
        return;
      }
    }
    try {
      const { data, error } = await supabaseData
        .from('vendors')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        if (retryCount < 1) {
          console.warn('VendorManagement: query error, retrying with session refresh...', error.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 1000));
          return loadVendors(retryCount + 1);
        }
        throw error;
      }
      setVendors(data || []);
      // Cache on success
      if (isOfflineModeEnabled()) {
        cacheData('vendors', companyId, data || []);
      }
    } catch (error) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cached = getCachedData('vendors', companyId);
        if (cached) {
          setVendors(cached);
          setLoading(false);
          return;
        }
      }
      // Table might not exist yet - that's okay
      setVendors([]);
    }
    setLoading(false);
  };

  const handleSaveVendor = async () => {
    // Mark all validated fields as touched so inline errors appear
    setFieldsTouched({ name: true, category: true });

    if (!formData.name.trim()) {
      alert('Vendor name is required');
      return;
    }
    if (!formData.category) {
      alert('Please select a vendor category');
      return;
    }
    if (savingVendor) return;
    setSavingVendor(true);

    try {
      if (editingVendor) {
        const { error } = await supabaseData
          .from('vendors')
          .update(formData)
          .eq('id', editingVendor.id);
        if (error) throw error;
      } else {
        // Check for duplicate vendor name
        const existing = vendors.find(v => v.name.toLowerCase().trim() === formData.name.toLowerCase().trim());
        if (existing) {
          alert(`A vendor named "${existing.name}" already exists.`);
          setSavingVendor(false);
          return;
        }
        const { error } = await supabaseData
          .from('vendors')
          .insert([{ ...formData, company_id: companyId }]);
        if (error) throw error;
      }
      loadVendors();
      closeModal();
    } catch (error) {
      alert('Failed to save vendor. The vendors table may need to be created in the database.');
    } finally {
      setSavingVendor(false);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      const { error } = await supabaseData
        .from('vendors')
        .delete()
        .eq('id', vendorId);
      if (error) throw error;
      loadVendors();
    } catch (error) {

    }
  };

  const openMapToVendor = (vendor) => {
    if (!vendor.address) {
      alert('No address on file for this vendor');
      return;
    }
    const encoded = encodeURIComponent(vendor.address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingVendor(null);
    setFieldsTouched({});
    setFormData({
      name: '',
      contact_name: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      notes: '',
      category: ''
    });
  };

  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      contact_name: vendor.contact_name || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      website: vendor.website || '',
      notes: vendor.notes || '',
      category: vendor.category || ''
    });
    setShowAddModal(true);
  };

  const filteredVendors = vendors.filter(v =>
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = ['Parts Supplier', 'Equipment', 'Materials', 'Tools', 'Subcontractor', 'Other'];

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
            Vendor Management
          </h1>
          <p style={{ color: colors.textMuted }}>
            Track your suppliers, parts vendors, and subcontractors
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: colors.accent,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Add Vendor
        </button>
      </div>

      {/* Search */}
      <div style={{
        position: 'relative',
        marginBottom: '1.5rem'
      }}>
        <Search size={18} style={{
          position: 'absolute',
          left: '1rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: colors.textMuted
        }} />
        <input
          type="text"
          placeholder="Search vendors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem 0.75rem 2.75rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            fontSize: '1rem'
          }}
        />
      </div>

      {/* Vendor List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
          Loading vendors...
        </div>
      ) : filteredVendors.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <Building2 size={48} style={{ color: colors.textMuted, marginBottom: '1rem' }} />
          <h3 style={{ marginBottom: '0.5rem' }}>No vendors yet</h3>
          <p style={{ color: colors.textMuted, marginBottom: '1rem' }}>
            Add your parts suppliers, equipment vendors, and subcontractors.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Add Your First Vendor
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1rem'
        }}>
          {filteredVendors.map(vendor => (
            <div
              key={vendor.id}
              style={{
                background: colors.bgCard,
                borderRadius: '12px',
                border: `1px solid ${colors.border}`,
                padding: '1.25rem',
                position: 'relative'
              }}
            >
              {/* Category Badge */}
              {vendor.category && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0.25rem 0.5rem',
                  background: `${colors.accent}20`,
                  color: colors.accent,
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {vendor.category}
                </div>
              )}

              {/* Vendor Name */}
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                marginBottom: '0.75rem',
                paddingRight: vendor.category ? '100px' : '0'
              }}>
                {vendor.name}
              </h3>

              {/* Contact Info */}
              <div style={{ marginBottom: '1rem' }}>
                {vendor.contact_name && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.375rem',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>
                    <Building2 size={14} />
                    {vendor.contact_name}
                  </div>
                )}
                {vendor.phone && (
                  <a
                    href={`tel:${vendor.phone}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.375rem',
                      color: colors.text,
                      fontSize: '0.9rem',
                      textDecoration: 'none'
                    }}
                  >
                    <Phone size={14} style={{ color: colors.accent }} />
                    {vendor.phone}
                  </a>
                )}
                {vendor.email && (
                  <a
                    href={`mailto:${vendor.email}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.375rem',
                      color: colors.text,
                      fontSize: '0.9rem',
                      textDecoration: 'none'
                    }}
                  >
                    <Mail size={14} style={{ color: colors.accent }} />
                    {vendor.email}
                  </a>
                )}
                {vendor.address && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    color: colors.textMuted,
                    fontSize: '0.85rem'
                  }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{vendor.address}</span>
                  </div>
                )}
              </div>

              {/* Notes */}
              {vendor.notes && (
                <div style={{
                  padding: '0.75rem',
                  background: colors.bgInput,
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: colors.textMuted,
                  marginBottom: '1rem'
                }}>
                  {vendor.notes}
                </div>
              )}

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '1rem',
                marginTop: 'auto'
              }}>
                <button
                  onClick={() => openMapToVendor(vendor)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem',
                    background: '#1a73e8',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  <MapPin size={14} />
                  Directions
                </button>
                {vendor.website && (
                  <a
                    href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem 0.75rem',
                      background: colors.bgInput,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      textDecoration: 'none'
                    }}
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  onClick={() => openEditModal(vendor)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5rem 0.75rem',
                    background: colors.bgInput,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDeleteVendor(vendor.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    color: '#e74c3c',
                    border: `1px solid #e74c3c`,
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Vendor Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., ABC Parts Supply"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Category <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  onBlur={() => setFieldsTouched(t => ({ ...t, category: true }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${!fieldsTouched.category || formData.category ? colors.border : '#ef4444'}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {fieldsTouched.category && !formData.category && (
                  <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    Category is required
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Your sales rep or contact"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@vendor.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City, State ZIP"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Website
                </label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.vendor.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: '500' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Account number, delivery terms, etc."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1.5rem'
            }}>
              <button
                onClick={closeModal}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: colors.bgInput,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVendor}
                disabled={savingVendor}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: savingVendor ? colors.border : colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: savingVendor ? 'not-allowed' : 'pointer',
                  opacity: savingVendor ? 0.7 : 1
                }}
              >
                <Save size={16} />
                {savingVendor ? 'Saving...' : editingVendor ? 'Save Changes' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
